/**
 * Home Maintenance sagas — async logic, API calls.
 */

import { call, put, take, race, delay, cancelled, takeLatest } from 'redux-saga/effects';
import {
  getAppliances,
  getAppliance,
  createAppliance as apiCreateAppliance,
  updateAppliance as apiUpdateAppliance,
  deleteAppliance as apiDeleteAppliance,
  getApplianceTodos,
  getServiceRecords,
  startApplianceResearch,
  getApplianceResearch,
  applyApplianceResearch,
} from '@/infra/api/client';
import type { ResearchResult } from '@/infra/api/client';
import { withSagaFlow, emitSagaPoint } from '@/features/qpl-client/saga-helpers';
import {
  setAppliances,
  upsertAppliance,
  removeAppliance,
  setServiceRecords,
  setApplianceTodos,
  setApplianceTodosLoading,
  setMaintenanceLoading,
  setMaintenanceError,
  setResearch,
  setResearchPolling,
  setResearchApplying,
  setResearchError,
  fetchApplianceTodos as fetchApplianceTodosAction,
  FETCH_APPLIANCES,
  FETCH_SERVICE_RECORDS,
  CREATE_APPLIANCE,
  UPDATE_APPLIANCE,
  DELETE_APPLIANCE,
  FETCH_APPLIANCE,
  FETCH_APPLIANCE_TODOS,
  START_RESEARCH,
  FETCH_RESEARCH,
  APPLY_RESEARCH,
  STOP_RESEARCH_POLL,
  ApplianceInput,
  ProposedTask,
} from './ducks';
import { Appliance, ServiceRecord, Todo } from '@/infra/types/domain';

// ─── Fetch Appliances ──────────────────────────────────────

export function* fetchAppliancesSaga(action: { type: string }): Generator {
  yield call(withSagaFlow, 'home_maintenance.fetch_appliances', ['maintenance'], action, function* () {
    yield put(setMaintenanceLoading(true));
    try {
      const appliances = (yield call(getAppliances)) as Appliance[];
      yield* emitSagaPoint('api_getAppliances_ok', { count: appliances.length });
      yield put(setAppliances(appliances));
      yield put(setMaintenanceError(null));
    } catch (error: any) {
      yield put(setMaintenanceError(error.message || 'Failed to fetch appliances'));
      yield* emitSagaPoint('api_getAppliances_err', { message: error?.message });
    } finally {
      yield put(setMaintenanceLoading(false));
    }
  });
}

// ─── Fetch Single Appliance ────────────────────────────────
// Detail-page entry point: hydrate the one appliance the user navigated to
// without requiring the full list to have loaded first.

export function* fetchApplianceSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'home_maintenance.fetch_appliance', ['maintenance'], action, function* () {
    try {
      const appliance = (yield call(getAppliance, action.payload)) as Appliance;
      yield* emitSagaPoint('api_getAppliance_ok', { appliance_id: appliance.id });
      yield put(upsertAppliance(appliance));
      yield put(setMaintenanceError(null));
    } catch (error: any) {
      yield put(setMaintenanceError(error.message || 'Failed to fetch appliance'));
      yield* emitSagaPoint('api_getAppliance_err', { message: error?.message });
    }
  });
}

// ─── Create Appliance ──────────────────────────────────────
// CreateAppliance returns the full canonical Appliance row; replaying it via
// upsertAppliance is the complete delta — no list refetch needed.
// See feedback_saga_full_delta.

export function* createApplianceSaga(action: { type: string; payload: ApplianceInput }): Generator {
  yield call(withSagaFlow, 'home_maintenance.create_appliance', ['maintenance'], action, function* () {
    try {
      const appliance = (yield call(apiCreateAppliance, action.payload)) as Appliance;
      yield* emitSagaPoint('api_createAppliance_ok', { appliance_id: appliance.id });
      yield put(upsertAppliance(appliance));
      yield put(setMaintenanceError(null));
    } catch (error: any) {
      yield put(setMaintenanceError(error.message || 'Failed to create appliance'));
      yield* emitSagaPoint('api_createAppliance_err', { message: error?.message });
    }
  });
}

// ─── Update Appliance ──────────────────────────────────────

export function* updateApplianceSaga(action: {
  type: string;
  payload: { id: string; input: ApplianceInput };
}): Generator {
  yield call(withSagaFlow, 'home_maintenance.update_appliance', ['maintenance'], action, function* () {
    try {
      const appliance = (yield call(
        apiUpdateAppliance,
        action.payload.id,
        action.payload.input,
      )) as Appliance;
      yield* emitSagaPoint('api_updateAppliance_ok', { appliance_id: appliance.id });
      yield put(upsertAppliance(appliance));
      yield put(setMaintenanceError(null));
    } catch (error: any) {
      yield put(setMaintenanceError(error.message || 'Failed to update appliance'));
      yield* emitSagaPoint('api_updateAppliance_err', { message: error?.message });
    }
  });
}

// ─── Delete Appliance ──────────────────────────────────────

export function* deleteApplianceSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'home_maintenance.delete_appliance', ['maintenance'], action, function* () {
    try {
      yield call(apiDeleteAppliance, action.payload);
      yield* emitSagaPoint('api_deleteAppliance_ok', { appliance_id: action.payload });
      yield put(removeAppliance(action.payload));
      yield put(setMaintenanceError(null));
    } catch (error: any) {
      yield put(setMaintenanceError(error.message || 'Failed to delete appliance'));
      yield* emitSagaPoint('api_deleteAppliance_err', { message: error?.message });
    }
  });
}

// ─── Fetch Appliance Todos ─────────────────────────────────

export function* fetchApplianceTodosSaga(action: { type: string; payload: string }): Generator {
  yield call(withSagaFlow, 'home_maintenance.fetch_appliance_todos', ['maintenance'], action, function* () {
    yield put(setApplianceTodosLoading(true));
    try {
      const todos = (yield call(getApplianceTodos, action.payload)) as Todo[];
      yield* emitSagaPoint('api_getApplianceTodos_ok', { count: todos.length });
      yield put(setApplianceTodos(action.payload, todos));
      yield put(setMaintenanceError(null));
    } catch (error: any) {
      yield put(setMaintenanceError(error.message || 'Failed to fetch appliance todos'));
      yield* emitSagaPoint('api_getApplianceTodos_err', { message: error?.message });
    } finally {
      yield put(setApplianceTodosLoading(false));
    }
  });
}

// ─── Fetch Service Records ─────────────────────────────────

export function* fetchServiceRecordsSaga(action: {
  type: string;
  payload: string;
}): Generator {
  yield call(withSagaFlow, 'home_maintenance.fetch_service_records', ['maintenance'], action, function* () {
    yield put(setMaintenanceLoading(true));
    try {
      const records = (yield call(getServiceRecords, action.payload)) as ServiceRecord[];
      yield* emitSagaPoint('api_getServiceRecords_ok', { count: records.length });
      yield put(setServiceRecords(records));
      yield put(setMaintenanceError(null));
    } catch (error: any) {
      yield put(setMaintenanceError(error.message || 'Failed to fetch service records'));
      yield* emitSagaPoint('api_getServiceRecords_err', { message: error?.message });
    } finally {
      yield put(setMaintenanceLoading(false));
    }
  });
}

// ─── Research: poll interval ───────────────────────────────
// 4s between GETs while a run is pending/running. Long enough to avoid
// hammering the agent workflow, short enough that "complete" surfaces quickly.
const RESEARCH_POLL_INTERVAL_MS = 4000;

function isTerminal(status: string): boolean {
  return status === 'complete' || status === 'failed';
}

// ─── Fetch Research (one-shot) ─────────────────────────────
// Loads the latest research run for an appliance on detail-page mount. If the
// run is still pending/running, hands off to the poll loop so the UI advances
// without a manual refresh. A 404 (no research yet) is the honest empty state,
// not an error.

export function* fetchResearchSaga(action: { type: string; payload: string }): Generator {
  const applianceId = action.payload;
  yield call(withSagaFlow, 'home_maintenance.fetch_research', ['maintenance'], action, function* () {
    try {
      const result = (yield call(getApplianceResearch, applianceId)) as ResearchResult;
      yield* emitSagaPoint('api_getResearch_ok', { status: result.status });
      yield put(setResearch(applianceId, result));
      if (!isTerminal(result.status)) {
        yield call(pollResearch, applianceId);
      }
    } catch (error: any) {
      // 404 = no research has run for this appliance; keep the empty state.
      const status = error?.response?.status;
      if (status === 404) {
        yield put(setResearch(applianceId, null));
        return;
      }
      yield put(setResearchError(applianceId, error?.message || 'Failed to load research'));
      yield* emitSagaPoint('api_getResearch_err', { message: error?.message });
    }
  });
}

// ─── Start Research ────────────────────────────────────────
// Dispatches a new run, then polls until it settles. takeLatest cancels any
// prior start for the same mount; the poll loop also races against an explicit
// STOP_RESEARCH_POLL (detail-page unmount) and the `finally`/`cancelled` guard
// clears the polling flag on any exit path.

export function* startResearchSaga(action: { type: string; payload: string }): Generator {
  const applianceId = action.payload;
  yield call(withSagaFlow, 'home_maintenance.start_research', ['maintenance'], action, function* () {
    yield put(setResearchError(applianceId, null));
    try {
      yield call(startApplianceResearch, applianceId);
      yield* emitSagaPoint('api_startResearch_ok', { appliance_id: applianceId });
    } catch (error: any) {
      yield put(setResearchError(applianceId, error?.message || 'Failed to start research'));
      yield* emitSagaPoint('api_startResearch_err', { message: error?.message });
      return;
    }
    yield call(pollResearch, applianceId);
  });
}

// pollResearch GETs the run on an interval until it reaches a terminal status
// or the loop is cancelled. The polling flag is set true on entry and cleared
// in the cancelled/terminal guard so the UI never gets stuck "in progress".
export function* pollResearch(applianceId: string): Generator {
  yield put(setResearchPolling(applianceId, true));
  try {
    while (true) {
      const result = (yield call(getApplianceResearch, applianceId)) as ResearchResult;
      yield put(setResearch(applianceId, result));
      if (isTerminal(result.status)) {
        return;
      }
      // Race the interval against an explicit stop so unmount tears the loop
      // down immediately rather than after the next tick.
      const { stopped } = (yield race({
        tick: delay(RESEARCH_POLL_INTERVAL_MS),
        stopped: take(
          (a: { type: string; payload?: unknown }) =>
            a.type === STOP_RESEARCH_POLL && a.payload === applianceId,
        ),
      })) as { tick?: true; stopped?: unknown };
      if (stopped) {
        return;
      }
    }
  } catch (error: any) {
    yield put(setResearchError(applianceId, error?.message || 'Research polling failed'));
    yield* emitSagaPoint('api_pollResearch_err', { message: error?.message });
  } finally {
    // Runs on terminal return, error, or saga cancellation (takeLatest restart
    // / STOP). Clearing here is the single source of truth for the flag.
    yield put(setResearchPolling(applianceId, false));
    if (yield cancelled()) {
      yield* emitSagaPoint('pollResearch_cancelled', { appliance_id: applianceId });
    }
  }
}

// ─── Apply Research ────────────────────────────────────────
// Materializes operator-reviewed tasks as recurring todos, then refetches the
// associated-todos list so the new rows appear without a page reload.

export function* applyResearchSaga(action: {
  type: string;
  payload: { applianceId: string; researchId: string; tasks: ProposedTask[] };
}): Generator {
  const { applianceId, researchId, tasks } = action.payload;
  yield call(withSagaFlow, 'home_maintenance.apply_research', ['maintenance'], action, function* () {
    yield put(setResearchApplying(applianceId, true));
    yield put(setResearchError(applianceId, null));
    try {
      const res = (yield call(applyApplianceResearch, applianceId, researchId, tasks)) as {
        created_todo_ids: string[];
      };
      yield* emitSagaPoint('api_applyResearch_ok', { created: res.created_todo_ids.length });
      // Refetch associated todos so the created recurring todos render in the
      // detail page's todos section (full-delta: server is the source of truth).
      yield put(fetchApplianceTodosAction(applianceId));
    } catch (error: any) {
      yield put(setResearchError(applianceId, error?.message || 'Failed to apply tasks'));
      yield* emitSagaPoint('api_applyResearch_err', { message: error?.message });
    } finally {
      yield put(setResearchApplying(applianceId, false));
    }
  });
}

// ─── Root Maintenance Saga ─────────────────────────────────

export function* maintenanceSaga(): Generator {
  yield takeLatest(FETCH_APPLIANCES, fetchAppliancesSaga);
  yield takeLatest(FETCH_APPLIANCE, fetchApplianceSaga);
  yield takeLatest(FETCH_SERVICE_RECORDS, fetchServiceRecordsSaga);
  yield takeLatest(CREATE_APPLIANCE, createApplianceSaga);
  yield takeLatest(UPDATE_APPLIANCE, updateApplianceSaga);
  yield takeLatest(DELETE_APPLIANCE, deleteApplianceSaga);
  yield takeLatest(FETCH_APPLIANCE_TODOS, fetchApplianceTodosSaga);
  // Research: takeLatest on both entry points so navigating between appliances
  // (or re-running) cancels the prior poll loop; the cancelled guard in
  // pollResearch clears the polling flag.
  yield takeLatest(START_RESEARCH, startResearchSaga);
  yield takeLatest(FETCH_RESEARCH, fetchResearchSaga);
  yield takeLatest(APPLY_RESEARCH, applyResearchSaga);
}
